/** AI pre-reply + customer insight (Dean, Oct 2026). Shown in the reading pane for an email matched to a customer file:
 *  the "what we know" box (file facts — payment habits, restructures, best channel, today's bank feed, P2P calendar…), the
 *  drafted reply and the handling guidance. Collapsible (remembered). Nothing is sent from here — "Use this draft" opens
 *  the normal Reply dialog with the text pre-filled. */
import { useState } from "react";
import { AlertTriangle, Bot, CalendarClock, ChevronDown, ChevronUp, Clock, Landmark, Lightbulb, Loader2, PenSquare, RefreshCw, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const TONE_CLS = {
  "payment chase": "bg-emerald-100 text-emerald-800 border-emerald-300",
  arrangement: "bg-violet-100 text-violet-800 border-violet-300",
  dispute: "bg-rose-100 text-rose-800 border-rose-300",
  hardship: "bg-amber-100 text-amber-800 border-amber-300",
  information: "bg-blue-100 text-blue-800 border-blue-300",
  holding: "bg-slate-100 text-slate-700 border-slate-300",
};
const LS_KEY = "sf_prereply_collapsed";

export function PreReplyCard({ email, api, matched, onTriaged, onUseDraft }) {
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_KEY) === "1");
  const pr = email.triage?.prereply;
  const ldOnly = !matched?.case_id && matched?.kind === "loandisk";
  if (!matched?.case_id && !ldOnly) return null;
  const toggle = () => { const v = !collapsed; setCollapsed(v); localStorage.setItem(LS_KEY, v ? "1" : "0"); };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.prereply(email.id);
      onTriaged(r.triage);
      toast.success("Draft ready — read it, tweak it, then press Send");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  if (!pr) {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 border-b bg-violet-50/40 text-[11px] text-violet-900" data-testid="prereply-empty">
        <Bot className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1">{ldOnly ? `Customer found in LoanDisk — let the AI read the loan and ${api.isAus ? "today's Capay / PayTo collections" : "today's bank feed"}, then draft a reply with guidance (no portal file needed).`
          : matched.in_arrears ? `Customer in arrears — let the AI read the file, ${api.isAus ? "today's Capay / PayTo collections" : "today's bank feed"} and the P2P calendar, then draft a reply with guidance.` : "Let the AI read the file and draft a reply with guidance."}</span>
        <Button size="sm" variant="outline" className="h-6 text-[10px] text-violet-700 border-violet-300 bg-white" onClick={generate} disabled={busy} data-testid="prereply-generate-btn">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}{busy ? "Reading the file…" : "Draft reply with AI"}
        </Button>
      </div>
    );
  }

  const bank = pr.bank_today;
  const header = (
    <div className="flex items-center gap-2 flex-wrap">
      <button type="button" onClick={toggle} className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-900 hover:text-violet-700" title={collapsed ? "Expand the AI pre-reply" : "Minimise the AI pre-reply"} data-testid="prereply-toggle">
        {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        <Bot className="w-3.5 h-3.5 text-violet-700" />AI pre-reply
      </button>
      {pr.tone && <Badge variant="outline" className={cn("text-[9px]", TONE_CLS[pr.tone] || TONE_CLS.holding)} data-testid="prereply-tone">{pr.tone}</Badge>}
      {bank?.checked && (
        <Badge variant="outline" className={cn("text-[9px] gap-0.5", bank.total > 0 ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-slate-50 text-slate-600 border-slate-300")}
          title={api.isAus ? "Loan Repayments — Capay bank credits + GoCardless PayTo / Direct Debit, today only" : "Daily Collections Report bank feed, today only"} data-testid="prereply-bank-badge">
          <Landmark className="w-2.5 h-2.5" />{bank.total > 0 ? `${api.currency}${Number(bank.total).toLocaleString("en-GB", { minimumFractionDigits: 2 })} ${api.isAus ? "received today" : "in today's bank feed"}` : (api.isAus ? "nothing received today" : "nothing in today's bank feed")}
        </Badge>
      )}
      {bank?.in_flight?.length > 0 && (
        <Badge variant="outline" className="text-[9px] gap-0.5 bg-sky-50 text-sky-800 border-sky-300" title={bank.in_flight.map((i) => `${i.scheme} ${api.currency}${i.amount} (${i.status})`).join(" · ")} data-testid="prereply-inflight-badge">
          <Clock className="w-2.5 h-2.5" />{`${api.currency}${Number(bank.in_flight_total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })} in flight`}
        </Badge>
      )}
      {bank?.failed?.length > 0 && (
        <Badge variant="outline" className="text-[9px] gap-0.5 bg-rose-50 text-rose-800 border-rose-300" title={bank.failed.map((i) => `${i.scheme} ${api.currency}${i.amount} on ${i.date}${i.failure ? ` — ${i.failure}` : ""}`).join(" · ")} data-testid="prereply-failed-badge">
          <AlertTriangle className="w-2.5 h-2.5" />{`${bank.failed.length} failed collection${bank.failed.length === 1 ? "" : "s"} (7 days)`}
        </Badge>
      )}
      {bank?.next_collection && (
        <Badge variant="outline" className="text-[9px] gap-0.5 bg-slate-50 text-slate-700 border-slate-300" title="Next scheduled GoCardless AU collection" data-testid="prereply-next-badge">
          <CalendarClock className="w-2.5 h-2.5" />{`next ${api.currency}${Number(bank.next_collection.amount).toLocaleString("en-GB", { minimumFractionDigits: 2 })} · ${bank.next_collection.date}`}
        </Badge>
      )}
      {pr.used_policy && <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-300" title="The uploaded policy documents were given to the AI" data-testid="prereply-policy-badge">policy applied</Badge>}
      {collapsed && <span className="text-[10px] text-muted-foreground">{(pr.insight || []).length} insight · {(pr.guidance || []).length} guidance</span>}
      {!collapsed && <span className="text-[10px] text-muted-foreground">drafted by {pr.by || "AI"}{pr.generated_at ? ` · ${new Date(pr.generated_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}` : ""} · nothing is sent until you press Send</span>}
      <div className="ml-auto flex items-center gap-1">
        {!collapsed && (
          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-600" onClick={generate} disabled={busy} title="Re-read the file and draft again" data-testid="prereply-regenerate-btn">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}Redraft
          </Button>
        )}
        <Button size="sm" className="h-6 text-[10px] bg-violet-700 hover:bg-violet-800 text-white" onClick={() => onUseDraft(pr.draft)} data-testid="prereply-use-btn">
          <PenSquare className="w-3 h-3 mr-1" />Use this draft
        </Button>
      </div>
    </div>
  );

  return (
    <div className={cn("border-b bg-violet-50/40 px-4 space-y-2", collapsed ? "py-1.5" : "py-2.5")} data-testid="prereply-card" data-collapsed={collapsed ? "1" : "0"}>
      {header}
      {!collapsed && (
        <div className="grid gap-2 lg:grid-cols-2">
          <div className="rounded border border-violet-200 bg-white p-2.5" data-testid="prereply-insight">
            <div className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold flex items-center gap-1 mb-1"><Sparkles className="w-3 h-3" />What we know about this customer</div>
            <ul className="space-y-0.5 text-[11px] text-slate-800 list-disc pl-4">
              {(pr.insight || []).map((b, i) => <li key={i} data-testid={`prereply-insight-${i}`}>{b}</li>)}
              {!(pr.insight || []).length && <li className="text-muted-foreground list-none pl-0">No file history beyond the balance.</li>}
            </ul>
            {(pr.guidance || []).length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold flex items-center gap-1 mt-2 mb-1"><Lightbulb className="w-3 h-3" />Handling guidance</div>
                <ul className="space-y-0.5 text-[11px] text-slate-800 list-disc pl-4">
                  {pr.guidance.map((g, i) => <li key={i} data-testid={`prereply-guidance-${i}`}>{g}</li>)}
                </ul>
              </>
            )}
          </div>
          <div className="rounded border border-violet-200 bg-white p-2.5 text-[12px] leading-relaxed text-slate-800 whitespace-pre-wrap max-h-56 overflow-y-auto" data-testid="prereply-draft">
            {pr.draft || <span className="text-muted-foreground italic">No draft returned — press Redraft.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
